import { useMemo, useEffect, useCallback } from 'react';

import {
  ApolloQueryResult,
  FieldFunctionOptions,
  makeVar,
  QueryHookOptions,
  QueryOptions,
  Reference,
  SubscriptionHookOptions,
  useApolloClient,
  useQuery,
  useReactiveVar,
  useSubscription,
} from '@apollo/client';
import type { InvalidationPolicyCache } from '@nerdwallet/apollo-cache-policies';
import { DocumentNode } from 'graphql';

import { getCache } from '../cache';
import { generateQueryFromFragment, getFragmentTypename } from '../utils';

export type CreatePaginationHookOptions = {
  paginationFieldName: string;
  fragment: DocumentNode;
  filter: (
    ref: Reference,
    readField: InvalidationPolicyCache['readField'],
    options: FieldFunctionOptions
  ) => boolean;
  sort: (
    refA: Reference,
    refB: Reference,
    readField: InvalidationPolicyCache['readField']
  ) => number;
  isRefresh: boolean;
};

type PaginationContext<TItem = unknown> = {
  isInitialized: boolean;
  loading: boolean;
  start: { loading?: boolean; cursor?: TItem; lastCall?: Date };
  end: { loading?: boolean; cursor?: TItem; lastCall?: Date };
};

/**
 * Factory pour crééer les hooks pour une pagination par curseur.
 */
export const createPaginationHook = <TItem = unknown>({
  paginationFieldName,
  fragment,
  filter,
  sort,
  isRefresh,
}: CreatePaginationHookOptions) => {
  // Ajoute un nouveau champ dans le cache permettant de récupérer
  // une liste ordonnée et filtrée de référence.
  const cache = getCache();
  cache.policies.addTypePolicies({
    Query: {
      fields: {
        [paginationFieldName]: {
          read: (_, options) => {
            if (!options.variables?.isInitialized) {
              return undefined;
            }

            const items = cache.readReferenceWhere({
              __typename: getFragmentTypename(fragment),
              filter: (ref, readField) => {
                return filter(ref, readField, options);
              },
            });

            if (!items) {
              return undefined;
            }

            const sortedItems = [...items].sort((refA, refB) => {
              return sort(refA, refB, options.readField);
            });
            const startIndex = options.variables.startCursor
              ? sortedItems.findIndex(
                  ({ __ref }) =>
                    __ref ===
                    `${options.variables?.startCursor.__typename}:${options.variables?.startCursor.id}`
                )
              : 0;
            const endIndex = options.variables.endCursor
              ? sortedItems.findIndex(
                  ({ __ref }) =>
                    __ref ===
                    `${options.variables?.endCursor.__typename}:${options.variables?.endCursor.id}`
                ) + 1
              : sortedItems.length;

            return sortedItems.slice(startIndex, endIndex);
          },
        },
      },
    },
  });

  // Créé la query pour appliquer le fragment à la liste de référence
  const query = generateQueryFromFragment({
    fieldName: paginationFieldName,
    fragment,
  });

  const paginationContextVar = makeVar<PaginationContext<TItem>>({
    loading: true,
    isInitialized: false,
    start: {},
    end: {},
  });

  // Hook pour récupérer une liste de donnée entre deux curseurs.
  const usePaginateItems = <
    TData = any,
    TQuery extends QueryOptions = QueryOptions,
  >(options: {
    queryOptions: TQuery & { skip?: boolean };
    normalize: (data: ApolloQueryResult<TData>) => {
      startCursor?: TItem;
      endCursor?: TItem;
    };
  }) => {
    const apollo = useApolloClient();
    const paginationContext = useReactiveVar(paginationContextVar);

    const result = useQuery<
      { [x: string]: TItem[] },
      {
        isInitialized: boolean;
        startCursor?: TItem;
        endCursor?: TItem;
      }
    >(query, {
      variables: {
        ...(options.queryOptions.variables || {}),
        isInitialized: paginationContext.isInitialized,
        startCursor: !isRefresh ? paginationContext.start.cursor : undefined,
        endCursor: paginationContext.end.cursor,
      },
      fetchPolicy: 'cache-only',
    });

    useEffect(() => {
      paginationContextVar({
        loading: true,
        isInitialized: false,
        start: {},
        end: {},
      });

      if (!options.queryOptions.skip) {
        apollo.query(options.queryOptions).then((data) => {
          const { startCursor, endCursor } = options.normalize(data);
          paginationContextVar({
            ...paginationContext,
            start: { cursor: startCursor, lastCall: new Date() },
            end: { cursor: endCursor, lastCall: new Date() },
            loading: false,
            isInitialized: true,
          });
        });
      }
    }, [
      JSON.stringify(options.queryOptions.variables),
      options.queryOptions.skip,
    ]);

    return useMemo(
      () => ({
        items: result.data?.[paginationFieldName],
        loading: result.loading,
        error: result.error,
        called: result.called,
      }),
      [result.data, result.loading, result.error]
    );
  };

  const useLoadMore = <
    TData = any,
    TQuery extends QueryOptions = QueryOptions,
  >(options: {
    queryOptions: TQuery;
    normalize: (data: ApolloQueryResult<TData>) => TItem[] | undefined;
    normalizeVariables: (
      variables: TQuery['variables'],
      context: { loading?: boolean; cursor?: TItem; lastCall?: Date }
    ) => TQuery['variables'];
  }) => {
    const apollo = useApolloClient();
    const paginationContext = useReactiveVar(paginationContextVar);

    const execute = useCallback(
      (variables?: TQuery['variables'] & { limit?: number }) => {
        const limit = variables?.limit || 10;

        paginationContextVar({
          ...paginationContext,
          end: {
            ...(paginationContext.end || {}),
            loading: true,
          },
        });

        // TODO: readfromcache

        return apollo
          .query({
            ...options.queryOptions,
            variables: options.normalizeVariables(
              {
                ...(options.queryOptions.variables || {}),
                ...(variables || {}),
                limit,
              },
              paginationContext.end
            ),
          })
          .then((data) => {
            const newItems = options.normalize(data);
            paginationContextVar({
              ...paginationContext,
              end: {
                loading: false,
                cursor:
                  newItems && newItems.length >= limit
                    ? newItems[limit - 1]
                    : undefined,
                lastCall: new Date(),
              },
            });
          });
      },
      [options.queryOptions]
    );

    return useMemo(
      () => ({
        execute,
        status: {
          loading: paginationContext.end.loading,
          hasMore: !!paginationContext.end.cursor,
          cursor: paginationContext.end.cursor,
        },
      }),
      [execute, paginationContext.end.loading, paginationContext.end.cursor]
    );
  };

  const useLoadPrev = <
    TData = any,
    TQuery extends QueryOptions = QueryOptions,
  >(options: {
    queryOptions: TQuery;
    normalize: (data: ApolloQueryResult<TData>) => TItem[] | undefined;
    normalizeVariables: (
      variables: TQuery['variables'],
      context: { loading?: boolean; cursor?: TItem; lastCall?: Date }
    ) => TQuery['variables'];
  }) => {
    const apollo = useApolloClient();
    const paginationContext = useReactiveVar(paginationContextVar);

    const execute = useCallback(
      (variables?: TQuery['variables'] & { limit?: number }) => {
        const limit = variables?.limit || 10;

        paginationContextVar({
          ...paginationContext,
          start: {
            ...(paginationContext.start || {}),
            loading: true,
          },
        });

        // TODO: readfromcache

        return apollo
          .query({
            ...options.queryOptions,
            variables: options.normalizeVariables(
              {
                ...(options.queryOptions.variables || {}),
                ...(variables || {}),
                limit,
              },
              paginationContext.start
            ),
          })
          .then((data) => {
            const newItems = options.normalize(data);
            paginationContextVar({
              ...paginationContext,
              start: {
                loading: false,
                cursor:
                  newItems && newItems.length >= limit
                    ? newItems[limit - 1]
                    : undefined,
                lastCall: new Date(),
              },
            });
          });
      },
      [options.queryOptions, paginationContext]
    );

    return useMemo(
      () => ({
        execute,
        status: {
          loading: paginationContext.start.loading,
          hasMore: !!paginationContext.start.cursor,
          cursor: paginationContext.start.cursor,
        },
      }),
      [execute, paginationContext.start.loading, paginationContext.start.cursor]
    );
  };

  const useRefresh = <
    TData = any,
    TQuery extends QueryHookOptions = QueryHookOptions,
  >(options: {
    queryOptions: TQuery & { query: QueryOptions['query'] };
    normalize: (data: ApolloQueryResult<TData>) => TItem[] | undefined;
    normalizeVariables: (
      variables: TQuery['variables'],
      context: { loading?: boolean; cursor?: TItem; lastCall?: Date }
    ) => TQuery['variables'];
  }) => {
    const paginationContext = useReactiveVar(paginationContextVar);
    const { query, ...queryOptions } = options.queryOptions;

    const refreshQuery = useQuery(query, {
      ...queryOptions,
      variables: options.normalizeVariables(
        {
          ...(queryOptions.variables || {}),
        },
        paginationContext.start
      ),
    });

    const execute = useCallback(
      async (variables?: TQuery['variables']) => {
        paginationContextVar({
          ...paginationContext,
          start: {
            ...(paginationContext.start || {}),
            loading: true,
          },
        });

        return refreshQuery
          .refetch({
            ...options.queryOptions,
            variables: options.normalizeVariables(
              {
                ...(options.queryOptions.variables || {}),
                ...(variables || {}),
              },
              paginationContext.start
            ),
          })
          .then(({ data }) => {
            const newItems = options.normalize(data);
            paginationContextVar({
              ...paginationContext,
              start: {
                loading: false,
                cursor: newItems?.[0] || paginationContext.start.cursor,
                lastCall: new Date(),
              },
            });
          });
      },
      [options.queryOptions, paginationContext]
    );

    return useMemo(
      () => ({
        execute,
        status: {
          loading: paginationContext.start.loading,
          cursor: paginationContext.start.cursor,
        },
      }),
      [execute, paginationContext.start.loading, paginationContext.start.cursor]
    );
  };

  const useAutoRefresh = <
    TData = any,
    TQuery extends QueryHookOptions = QueryHookOptions,
    TSyncQuery extends SubscriptionHookOptions = SubscriptionHookOptions,
  >(options: {
    queryOptions: TQuery & { query: QueryOptions['query'] };
    syncQueryOptions: TSyncQuery & { query: QueryOptions['query'] };
    normalize: (data: ApolloQueryResult<TData>) => TItem[] | undefined;
    normalizeVariables: (
      variables: TQuery['variables'],
      context: { loading?: boolean; cursor?: TItem; lastCall?: Date }
    ) => TQuery['variables'];
    normalizeSyncVariables?: (
      variables: TSyncQuery['variables'],
      context: { loading?: boolean; cursor?: TItem; lastCall?: Date }
    ) => TQuery['variables'];
  }) => {
    const paginationContext = useReactiveVar(paginationContextVar);
    const refreshQuery = useRefresh({
      queryOptions: options.queryOptions,
      normalize: options.normalize,
      normalizeVariables: options.normalizeVariables,
    });
    const { query, variables, ...subscriptionOptions } =
      options.syncQueryOptions;

    useSubscription(query, {
      ...subscriptionOptions,
      variables: options.normalizeSyncVariables
        ? options.normalizeSyncVariables(
            variables || {},
            paginationContext.start
          )
        : variables,
      // La subscription pour les nouveaux posts est active seulement après le début du live si on laisse le skip
      // skip: !paginationContext.start.cursor,
      onData: () => {
        if (!refreshQuery.status.loading) {
          refreshQuery.execute();
        }
      },
    });

    return refreshQuery;
  };

  return {
    usePaginateItems,
    useLoadMore,
    useLoadPrev,
    useRefresh,
    useAutoRefresh,
  };
};
